import { API, graphqlOperation } from 'aws-amplify'
import constants from '../constants';
import { brandDynamic, clientDynamic, forgotPasswordDynamic, resetPasswordDynamic, cognitoInfo } from '../graphql/queries'
import { getStoredKey, setStoredKey, shouldUseCachedValue } from './storage-service'
import { AuthUserData } from "../models/auth-user-types";
import { getUser } from './user-service'
import {
    AuthenticationDetails,
    CognitoIdToken,
    CognitoRefreshToken,
    CognitoUserPool,
    CognitoUserSession
} from "amazon-cognito-identity-js";
const AmazonCognitoIdentity = require('amazon-cognito-identity-js');

export async function getBrandConfig(type: string, online: boolean) {
    const storedValue = await getStoredKey(constants.BRAND + type);
    
    if(shouldUseCachedValue(storedValue, online, true)){
        return storedValue;
    } else {
        const clientName = await getStoredKey(constants.CURRENT_CLIENT_NAME) || "callawaygolf";
        const result: any = await API.graphql(graphqlOperation(brandDynamic, {type: type, client: clientName}));
        await setStoredKey(constants.BRAND + type, result.data)
        return result.data;
    }
}

export async function sendForgotPasswordEmail(email: string): Promise<any> {
    const clientName = await getStoredKey(constants.CURRENT_CLIENT_NAME) || "callawaygolf";

    let body = {
        user: {
            email: email
        }
    }
    return API.graphql(graphqlOperation(forgotPasswordDynamic, {body: body, client: clientName}));
}

export async function getCognitoPoolInfo(): Promise<any> {
    const clientName = await getStoredKey(constants.CURRENT_CLIENT_NAME) || "callawaygolf";
    return API.graphql(graphqlOperation(cognitoInfo, {client: clientName}));
}

export async function changePassword(token: string, password: string): Promise<any> {
    const clientName = await getStoredKey(constants.CURRENT_CLIENT_NAME) || "callawaygolf";

    let body = {
        user: {
            password: password,
            password_confirmation: password,
            reset_password_token: token
        }
    }
    return API.graphql(graphqlOperation(resetPasswordDynamic, {body: body, client: clientName}));
}

export async function getClientConfig(clientName: string) {
    const storedValue = await getStoredKey(constants.CLIENT + clientName);
    
    if(storedValue){
        return storedValue;
    } else {
        const result: any = await API.graphql(graphqlOperation(clientDynamic, {client: clientName}));
        await setStoredKey(constants.CLIENT + clientName, result.data)
        return result.data;
    }
}

const authorize = (authenticationDetails: AuthenticationDetails, pool: CognitoUserPool): Promise<CognitoUserSession> => {
    const userData = {
        Username: authenticationDetails.getUsername(),
        Pool: pool,
    };

    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    return new Promise((resolve, reject) => {
        cognitoUser?.authenticateUser(authenticationDetails, {
            onSuccess: (session: CognitoUserSession) => {
                console.log("session", session)
                resolve(session);
            },
            onFailure: (error: any) => {
                console.log(error)
                reject(error) 
            },
        });
    })
}

export async function authUser(params: AuthUserData, remember: boolean, online: boolean) {

    try{
        const pool = await getCognitoPool();

        const {username, password} = params

        const authenticationData = {
            Username: username,
            Password: password,
        };

        const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
            authenticationData
        );

        const session = await authorize(authenticationDetails, pool)
        await setUserLoggedInDetails(session, remember, online);

        return true
    } catch (error: any){
        console.log("error", error)
        return false;
    }
}

async function setUserLoggedInDetails(authDetails: CognitoUserSession, remember: boolean, online: boolean) {
    const id_token: CognitoIdToken = authDetails.getIdToken();
    const refresh_token = authDetails.getRefreshToken().getToken();

    if(remember){
        setStoredKey(constants.REFERESH_TOKEN, refresh_token)
    }

    // TODO: For Phase 2/3 figure out where to get user language code from
    setStoredKey(constants.USER_LANGUAGE_CODE, 'en')

    await getUser(id_token.getJwtToken(), online);
}

const getCognitoUser = async () => {
    let cognitoPool = await getCognitoPool();

    return cognitoPool?.getCurrentUser();
}

export const getRegularUserPool = async () => {
    try {
        let availiblePools = await getCognitoPoolInfo();
        let userPool = null; 

        if(availiblePools.data && availiblePools.data.cognitoInfo) {
            availiblePools.data.cognitoInfo.forEach((pool: any) => {
                if(pool.name == "users") {
                    userPool = pool;
                }    
            });
        }

        return userPool;
    } catch {
        return false;
    }
}

const getCognitoPool = async () => {
    try {
        let poolId = await getStoredKey(constants.COGNITO_POOL_ID);
        let clientId = await getStoredKey(constants.COGNITO_CLIENT_ID);

        if(poolId && clientId) {
            return new AmazonCognitoIdentity.CognitoUserPool({
                UserPoolId: poolId,
                ClientId: clientId
            });
        } else {
            console.log("no pool or client ID found");
            return null;
        }
    } catch {
        return null;
    }
}

export const getSession = async () => {
    const cognitoUser = await getCognitoUser();

    return cognitoUser?.getSession((error: Error, result: CognitoUserSession | null) => {
        if (error) {
            console.error('Error getting session', JSON.stringify(error));
        }
        return result;
    });

};

export const cognitoLogout = async () => {
    const cognitoUser = await getCognitoUser();
    await cognitoUser?.signOut();

    return '/login';
}

export const refreshSession = async () => {
    const cognitoUser = await getCognitoUser();
    const refreshToken = await getStoredKey(constants.REFERESH_TOKEN);
    if(!refreshToken) {
        return false;
    }

    return new Promise((resolve, reject) => {
        const cognitoRefreshToken = new CognitoRefreshToken({RefreshToken: refreshToken});

        cognitoUser?.refreshSession(cognitoRefreshToken, (error: Error, result: CognitoUserSession | null) => {
            if (error) {
                console.error('Error refreshing token', JSON.stringify(error));
                reject(error);
            }

            resolve(result?.getAccessToken()?.getJwtToken());
        })
    })
}

export const getJwt = async () => {
    let session = await getSession();

    try {
      if (session && session?.isValid()) {
        return session.idToken.jwtToken;
      } else {
        console.log("trying refresh")
        await refreshSession();
        session = await getSession();

        if (session && session?.isValid()) {
          console.log("refresh worked")
          return session.idToken.jwtToken;
        }
      }

    } catch (catchError: any) {
      alert("Please logout and log back in to continue.")
    }
  };